import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EventCard from '../../../components/2025/EventCard';
import styles from '../../../components/2025/PicupEvent/styles.module.scss';
import { fetchEventById } from '../../../services/eventService';
import { optimizeImage } from '../../../utils/imageOptimizer';

type EventData = {
  imageUrl: string;
  title: string;
  time: string;
  location: string;
  description: string;
  labels: string[];
};

const PreviewPage2025: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    const id = searchParams.get('id') || searchParams.get('contentId');
    const draftKey = searchParams.get('draftKey') || undefined;

    // id, draftKey の両方が無い場合はイベント一覧へリダイレクト
    if (!id && !draftKey) {
      navigate('/2025/events', { replace: true });
      return;
    }

    // draftKey は任意。id があれば、公開済みデータを取得可能
    if (!id) {
      setError('プレビューに必要なパラメータが不足しています（id）');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const event = await fetchEventById(id, draftKey);
        if (!event) {
          setError('イベントの取得に失敗しました');
          return;
        }

        const mapped: EventData = {
          imageUrl: event.event_image?.url
            ? optimizeImage(event.event_image.url, { width: 800, quality: 80 })
            : '',
          title: event.event_title,
          time: event.event_time,
          location: event.event_location,
          description: String(
            (event as unknown as { event_description?: unknown })?.event_description || '',
          ),
          labels: [...event.event_category, ...event.event_day],
        };

        setEventData(mapped);
      } catch {
        setError('イベントの取得でエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [searchParams, navigate]);

  // モーダル表示時にbodyのスクロールを無効化
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return createPortal(
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.preventDefault()}
    >
      <div className={styles.modalContent}>
        {loading && (
          <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>読み込み中...</div>
        )}
        {!loading && error && (
          <div style={{ background: '#fff', padding: 24, borderRadius: 8, color: 'red' }}>
            {error}
          </div>
        )}
        {!loading && !error && eventData && (
          <EventCard events={[eventData]} onClose={() => navigate('/2025/events')} />
        )}
      </div>
    </div>,
    document.body,
  );
};

export default PreviewPage2025;
